import { toImageSrc } from "@/lib/utils";
import type { AlbumImage, AlbumLayout } from "@/stores/use-album-modal-store";

export function mediaToAlbumImage(m: {
  url: string
  formats?: {
    thumbnail?: { url: string }
    small?: { url: string }
    medium?: { url: string }
    large?: { url: string }
  }
  alternativeText?: string
  name: string
  id: number
}): AlbumImage {
  const f = m.formats
  return {
    url: toImageSrc(m.url),
    // Ảnh hiển thị: ưu tiên large → medium → small, fallback ảnh gốc.
    // Tránh dùng thumbnail (~156px) vì bị kéo giãn full width nên rất mờ.
    display: toImageSrc(f?.large?.url || f?.medium?.url || f?.small?.url || m.url),
    thumbnail: toImageSrc(f?.thumbnail?.url || m.url),
    alt: m.alternativeText || m.name || "",
    name: m.name,
    id: m.id,
  }
}

type ColumnDef = { width: number; stack: number }
type RowPattern = { columns: ColumnDef[] }

const DEFAULT_MASONRY_PATTERN: RowPattern[] = [
  { columns: [{ width: 50, stack: 1 }, { width: 20, stack: 2 }, { width: 30, stack: 1 }] },
  { columns: [{ width: 33.34, stack: 1 }, { width: 33.33, stack: 1 }, { width: 33.33, stack: 1 }] },
  { columns: [{ width: 50, stack: 1 }, { width: 50, stack: 1 }] },
]

function buildMasonryHTML(images: AlbumImage[], pattern: RowPattern[] = DEFAULT_MASONRY_PATTERN, rowHeight = 260, gap = 8): string {
  let queue = [...images]
  let rowsHTML = ""
  let patternIndex = 0

  while (queue.length > 0) {
    const row = pattern[patternIndex % pattern.length]
    patternIndex++

    let rowInner = ""
    let usedAny = false

    for (const col of row.columns) {
      if (queue.length === 0) break

      const stackImages = queue.splice(0, col.stack)
      if (stackImages.length === 0) continue
      usedAny = true

      const actualStack = stackImages.length

      const imgsHTML = stackImages.map(img => {
        const src = img.display || img.url
        const alt = img.alt || img.name || ""
        return `<div style="flex:1 1 0;overflow:hidden;">
          <img class="album-img" src="${src}" data-full-src="${img.url}" alt="${alt}" style="width:100%;height:100%;object-fit:cover;display:block;" loading="lazy" />
        </div>`
      }).join(actualStack > 1 ? `<div style="height:${gap}px;"></div>` : "")

      rowInner += `<div style="width:${col.width}%;height:100%;display:flex;flex-direction:column;overflow:hidden;">${imgsHTML}</div>`
    }

    if (!usedAny) break

    rowsHTML += `<div style="display:flex;width:100%;height:${rowHeight}px;gap:${gap}px;margin-bottom:${gap}px;">${rowInner}</div>`
  }

  return `<div class="ck-album-masonry">${rowsHTML}</div>`
}

export function buildAlbumHTML(images: AlbumImage[], layout: AlbumLayout, masonryPattern?: RowPattern[]): string {
  if (!images.length) return ""

  // Không nhúng <script>/<style> inline nữa:
  // - Lightbox mở qua window.openAlbumLightbox (component ContentLightbox) + delegated
  //   click trên .content-wrapper; ảnh mang data-full-src để phóng bản gốc.
  // - CSS lightbox + hover đặt ở globals.css.
  // Nhờ vậy album là HTML "sạch", không phụ thuộc script chạy được hay không.
  const baseClass = "ck-image-album"
  const layoutClass = `ck-album-${layout}`
  let inner = ""

  if (layout === "grid") {
    const cols = images.length <= 2 ? images.length : images.length <= 6 ? 3 : 4
    inner = `<div class="ck-album-grid" style="display:grid;grid-template-columns:repeat(${cols},1fr);gap:8px;">`
    images.forEach(img => {
      const src = img.display || img.url
      const alt = img.alt || img.name || ""
      inner += `<img class="album-img" src="${src}" data-full-src="${img.url}" alt="${alt}" style="width:100%;aspect-ratio:1;object-fit:cover;border-radius:4px;" loading="lazy" />`
    })
    inner += `</div>`
  } else if (layout === "masonry") {
    inner = buildMasonryHTML(images, masonryPattern)
  } else {
    inner = `<div class="ck-album-slider" style="position:relative;overflow:hidden;border-radius:8px;width:100%;">`
    const navButtons = images.length > 1
      ? `<button class="ck-slider-prev" style="position:absolute;top:50%;left:12px;transform:translateY(-50%);color:#fff;border:none;border-radius:50%;width:40px;height:40px;cursor:pointer;font-size:18px;z-index:1;display:flex;align-items:center;justify-content:center;" onclick="const t=this.closest('.ck-album-slider').querySelector('.ck-slider-track');const cur=parseInt(t.dataset.current||'0');const next=(cur-1+${images.length})%${images.length};t.style.transform='translateX(-'+next+'00%)';t.dataset.current=next;this.closest('.ck-album-slider').querySelectorAll('.ck-dot').forEach((d,i)=>d.style.background=i===next?'#fff':'rgba(255,255,255,0.5)');">&#10094;</button>`
      + `<button class="ck-slider-next" style="position:absolute;top:50%;right:12px;transform:translateY(-50%);color:#fff;border:none;border-radius:50%;width:40px;height:40px;cursor:pointer;font-size:18px;z-index:1;display:flex;align-items:center;justify-content:center;" onclick="const t=this.closest('.ck-album-slider').querySelector('.ck-slider-track');const cur=parseInt(t.dataset.current||'0');const next=(cur+1)%${images.length};t.style.transform='translateX(-'+next+'00%)';t.dataset.current=next;this.closest('.ck-album-slider').querySelectorAll('.ck-dot').forEach((d,i)=>d.style.background=i===next?'#fff':'rgba(255,255,255,0.5)');">&#10095;</button>`
      : ""
    const dots = images.length > 1
      ? `<div style="position:absolute;bottom:12px;left:50%;transform:translateX(-50%);display:flex;gap:6px;z-index:1;">` + images.map((_, i) => `<span class="ck-dot" style="width:8px;height:8px;border-radius:50%;background:${i === 0 ? "#fff" : "rgba(255,255,255,0.5)"};cursor:pointer;" onclick="const t=this.closest('.ck-album-slider').querySelector('.ck-slider-track');t.style.transform='translateX(-${i}00%)';t.dataset.current='${i}';this.closest('.ck-album-slider').querySelectorAll('.ck-dot').forEach((d,idx)=>d.style.background=idx===${i}?'#fff':'rgba(255,255,255,0.5)');"></span>`).join("") + `</div>`
      : ""
    inner += navButtons
    inner += `<div class="ck-slider-track" style="display:flex;transition:transform 0.4s ease;" data-current="0">`
    images.forEach(img => {
      const src = img.display || img.url
      const alt = img.alt || img.name || ""
      inner += `<div style="flex:0 0 100%;display:flex;align-items:center;justify-content:center;">`
        ; inner += `<img class="album-img" src="${src}" data-full-src="${img.url}" alt="${alt}" style="width:100%;height:auto;object-fit:contain;" loading="lazy" />`
      inner += `</div>`
    })
    inner += `</div>`
    inner += dots
  }

  return `<div class="${baseClass} ${layoutClass}" data-layout="${layout}" style="margin:16px 0;">${inner}</div>`
}